import {
  PendingAction,
  PENDING_ACTIONS_STORAGE_KEY,
  getExpiryForActionType,
} from "../types/pendingActions";

/**
 * Get all pending actions from sessionStorage, filtering out expired ones
 */
export const getAll = (): PendingAction[] => {
  try {
    const stored = sessionStorage.getItem(PENDING_ACTIONS_STORAGE_KEY);
    if (!stored) return [];

    const actions: PendingAction[] = JSON.parse(stored);
    const now = Date.now();

    // Filter out expired actions (except account_setup which has special handling)
    return actions.filter((action) => {
      const expiryTime = getExpiryForActionType(action.actionType);
      const isExpired = now - action.timestamp > expiryTime;

      // Keep non-expired actions, or expired account_setup (has special handling)
      return !isExpired || action.actionType === "account_setup";
    });
  } catch (error) {
    console.error("Error reading pending actions from storage:", error);
    return [];
  }
};

/**
 * Save all pending actions to sessionStorage
 */
export const saveAll = (actions: PendingAction[]): void => {
  try {
    sessionStorage.setItem(PENDING_ACTIONS_STORAGE_KEY, JSON.stringify(actions));
  } catch (error) {
    console.error("Error saving pending actions to storage:", error);
  }
};

/**
 * Add a single pending action
 */
export const add = (action: PendingAction): void => {
  const actions = getAll();
  // Avoid duplicates by id (not signature, since batch items share signatures)
  const exists = actions.some((a) => a.id === action.id);
  if (!exists) {
    actions.push(action);
    saveAll(actions);
  }
};

/**
 * Remove a pending action by signature
 */
export const removeBySignature = (signature: string): void => {
  const actions = getAll();
  const filtered = actions.filter((a) => a.signature !== signature);
  saveAll(filtered);
};

/**
 * Update the status of a pending action
 */
export const updateStatus = (signature: string, status: PendingAction["status"]): void => {
  const actions = getAll();
  const updated = actions.map((a) => (a.signature === signature ? { ...a, status } : a));
  saveAll(updated);
};

/**
 * Clear all pending actions
 */
export const clear = (): void => {
  try {
    sessionStorage.removeItem(PENDING_ACTIONS_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing pending actions from storage:", error);
  }
};

/**
 * Get pending actions for a specific destination address
 */
export const getForDestination = (destinationAddress: string): PendingAction[] => {
  const actions = getAll();
  return actions.filter((a) => a.destinationAddress === destinationAddress);
};
